import { useState } from 'react';
import { X, Copy, Check, Download, ExternalLink } from 'lucide-react';
import type { SavedQuery } from '../../../types/query';
import { swaggerService } from '../../../services/swaggerService';
import { PUBLIC_API_URL } from '../../../config/api';
import SwaggerEndpointSection from './SwaggerEndpointSection';

interface SwaggerModalProps {
  isOpen: boolean;
  onClose: () => void;
  query: SavedQuery;
}

export default function SwaggerModal({ isOpen, onClose, query }: SwaggerModalProps) {
  const [copiedSpec, setCopiedSpec] = useState(false);

  if (!isOpen) return null;

  const specJson = swaggerService.generateSpecJson(query);

  const handleCopySpec = async () => {
    try {
      await navigator.clipboard.writeText(specJson);
      setCopiedSpec(true);
      setTimeout(() => setCopiedSpec(false), 2000);
    } catch { /* ignore */ }
  };

  const handleDownloadSpec = () => {
    const blob = new Blob([specJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${query.endpoint_slug}-swagger.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const endpointUrl = `${PUBLIC_API_URL}/api/services/${query.endpoint_slug}`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex-shrink-0 border-b border-gray-200">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.182a9.818 9.818 0 110 19.636 9.818 9.818 0 010-19.636zM8.27 7.636a1.09 1.09 0 00-1.09 1.091v6.546a1.09 1.09 0 002.181 0V8.727a1.09 1.09 0 00-1.09-1.09zm3.82 0a1.09 1.09 0 00-1.09 1.091v6.546a1.09 1.09 0 002.18 0V8.727a1.09 1.09 0 00-1.09-1.09zm3.819 0a1.09 1.09 0 00-1.091 1.091v6.546a1.09 1.09 0 002.182 0V8.727a1.09 1.09 0 00-1.091-1.09z"/>
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900 truncate">{query.name}</h2>
                <p className="text-xs text-gray-500">Swagger</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 pb-3 flex items-center gap-2 flex-wrap">
            
            <button
              type="button"
              onClick={handleCopySpec}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {copiedSpec ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedSpec ? 'Copiado!' : 'Copiar JSON'}
            </button>
            <button
              type="button"
              onClick={handleDownloadSpec}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Download swagger.json
            </button>

            <div className="ml-auto flex items-center gap-2">
              {query.is_active ? (
                <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 uppercase tracking-wide">
                  Ativo
                </span>
              ) : (
                <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500 uppercase tracking-wide">
                  Inativo
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-sm">
              <span className="text-gray-500">Server: </span>
              <code className="text-sm font-mono text-gray-800">{PUBLIC_API_URL}</code>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-3">Endpoints</h3>
            <SwaggerEndpointSection query={query} />
          </div>
        </div>
      </div>
    </div>
  );
}
